import React, { MutableRefObject, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { compose, isNil, not, prop } from "ramda";
import { styled } from "styled-components";

const TocInner = styled.div`
  ${({ theme }) => theme.breakpoints.up("md")} {
    position: sticky;
    top: 1rem;
  }
`;

const TocLink = styled.a<{ $active?: boolean }>`
  display: block;
  margin-bottom: 0.5rem;
  line-height: 1.3rem;
  color: ${({ $active, theme }) => ($active ? theme.colours.primary : theme.colours.grey[400])};
`;

export const useScrollSpy = (toc: Maybe<TocElements>, options: IntersectionObserverInit) => {
  const [activeId, setActiveId] = React.useState<string>();
  const observer = React.useRef<Maybe<IntersectionObserver>>(null);
  const visibilityMap = useMemo(() => new Map(toc?.map(({ section }) => [section, false]) ?? []), [toc]);
  React.useEffect(() => {
    const elements = toc?.map(prop("section"));
    observer.current?.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        visibilityMap.set(entry.target as HTMLDivElement, entry.isIntersecting);
      });
      Array.from(visibilityMap.entries()).some(([entry, entryVisible]) => {
        if (entryVisible) {
          setActiveId(entry.id.replace("-section", ""));
          return true;
        }
      });
    }, options);
    elements?.forEach((el) => {
      if (el) {
        observer.current?.observe(el);
      }
    });
    return () => observer.current?.disconnect();
  }, [toc, options, visibilityMap]);
  return activeId;
};

type TocElements = { section: HTMLDivElement; title: string; anchor: string }[];

export const TocContext = React.createContext<{ toc: TocElements }>({
  toc: [],
});
export const TocProvider: React.FC<PropsWithChildren<{ docRef: MutableRefObject<Maybe<HTMLDivElement>> }>> = ({
  children,
  docRef,
}) => {
  const [tocElements, setTocElements] = useState<TocElements>([]);
  useEffect(() => {
    if (!isNil(docRef?.current)) {
      const sections = Array.from(docRef.current.querySelectorAll<HTMLDivElement>("[data-doc=section][id]"));
      const tocObjects = sections
        .map((sectionEl) => {
          const title = sectionEl.querySelector("[data-doc=title]");
          return !isNil(title) ? { section: sectionEl, title: title.textContent, anchor: title.id } : null;
        })
        .filter(compose(not, isNil)) as TocElements;
      setTocElements(tocObjects);
    }
  }, [docRef]);
  return <TocContext.Provider value={{ toc: tocElements }}>{children}</TocContext.Provider>;
};

export const Toc: React.FC = () => {
  const { toc } = useContext(TocContext);
  const activeId = useScrollSpy(toc, { rootMargin: "0% 0% 0% 0%" });
  return (
    <TocInner>
      {toc.map((tocElement) => (
        <TocLink key={tocElement.anchor} $active={activeId === tocElement.anchor} href={`#${tocElement.anchor}`}>
          {tocElement.title}
        </TocLink>
      ))}
    </TocInner>
  );
};
